// oauthApi — typed fetchers for /v1/oauth/* (MCP OAuth 2.1 consent surface).
//
// docs/specs/mcp-read-only.md §13 PR-D
//
// authorize 系列以 dashboard session cookie 認證；consents 列表 / 撤銷
// 同樣。raw OAuth wire endpoints (/oauth/token, /oauth/register,
// /oauth/revoke, /.well-known/*) 不在這裡 — 那些走 mcp-api 直接、不
// 經 dashboard。

import { apiFetch } from "../../lib/api-client.js";

export interface AuthorizeMetadata {
  client: {
    client_id: string;
    client_name: string;
    software_id: string | null;
    /** When this OAuth client was first DCR-registered. Lets the user
     *  recognise a familiar client ("ChatGPT 我 30 號才剛接過") vs a
     *  brand-new attempt. */
    created_at: string;
    /** Last token issue OR last verifyBearer hit. Null for a brand-new
     *  client that's only completed DCR (i.e. about to do its first
     *  consent). */
    last_used_at: string | null;
  };
  scope: string;
  redirect_uri: string;
  state: string;
  code_challenge: string;
  code_challenge_method: string;
  /** Country code (ISO 3166-1 alpha-2, or 'XX') of the USER's browser
   *  hitting /oauth/grant — Cloudflare-derived. Lets the user spot a
   *  phishing redirect from an unexpected geography. Null on local
   *  dev without CF in front. */
  requester_country: string | null;
}

export interface AuthorizeQueryInput {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  code_challenge: string;
  code_challenge_method: string;
  scope: string;
  state: string;
}

export interface ConsentRow {
  client_id: string;
  client_name: string;
  software_id: string | null;
  created_at: string;
  last_used_at: string | null;
  active_token_count: number;
}

export class OAuthApiError extends Error {
  status: number; code: string; correlationId: string | null; hint: string | null;
  constructor(status: number, code: string, hint: string | null, correlationId: string | null) {
    super(code);
    this.status = status; this.code = code; this.hint = hint; this.correlationId = correlationId;
  }
}

async function asError(r: Response): Promise<OAuthApiError> {
  let code = "request_failed"; let hint: string | null = null;
  let cid: string | null = r.headers.get("x-correlation-id");
  try {
    const b = (await r.json()) as { error?: string; hint?: string; correlation_id?: string };
    code = b.error ?? code; hint = b.hint ?? null; cid = b.correlation_id ?? cid;
  } catch { /* not JSON */ }
  return new OAuthApiError(r.status, code, hint, cid);
}

/** GET /v1/oauth/authorize — validates query params + returns metadata
 *  for the consent page to render. Throws on 4xx/5xx. */
export async function getAuthorizeMetadata(query: AuthorizeQueryInput): Promise<AuthorizeMetadata> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const r = await apiFetch(`/v1/oauth/authorize?${qs.toString()}`, {
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as AuthorizeMetadata;
}

/** POST /v1/oauth/authorize — user pressed 同意. Returns the redirect
 *  URL the browser should navigate to (claude.ai callback with ?code=). */
export async function grantAuthorization(query: AuthorizeQueryInput): Promise<string> {
  const r = await apiFetch(`/v1/oauth/authorize`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(query),
  });
  if (!r.ok) throw await asError(r);
  const b = (await r.json()) as { redirect_url: string };
  return b.redirect_url;
}

/** GET /v1/oauth/consents — list user's authorized OAuth clients. */
export async function listConsents(): Promise<ConsentRow[]> {
  const r = await apiFetch(`/v1/oauth/consents`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  const b = (await r.json()) as { items: ConsentRow[] };
  return b.items;
}

/** DELETE /v1/oauth/consents/:client_id — revoke all tokens + mark client revoked. */
export async function revokeConsent(clientId: string): Promise<number> {
  const r = await apiFetch(`/v1/oauth/consents/${encodeURIComponent(clientId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  const b = (await r.json()) as { revoked_tokens: number };
  return b.revoked_tokens;
}

export function friendlyOAuthError(e: unknown): string {
  if (!(e instanceof OAuthApiError)) {
    return e instanceof Error ? e.message : "OAuth 操作失敗。";
  }
  switch (e.code) {
    case "no_actor":
    case "no_user_id":
      return "需要登入後才能授權外部連線。";
    case "missing_permission":
      return "您沒有授權 MCP 連線的權限。";
    case "no_tenant_context":
      return e.hint ?? "您目前沒有租戶 context；super_admin 需先 impersonate 一個租戶。";
    case "unknown_client":
      return "OAuth client 不存在或已撤銷 — 請從 Claude 重新加入連線。";
    case "client_revoked":
      return "此 OAuth client 已被撤銷 — 請從 Claude 重新加入連線。";
    case "redirect_uri_mismatch":
      return "redirect_uri 與 client 註冊時不一致（可能被竄改）— 拒絕授權。";
    case "missing_code_challenge":
      return "OAuth 請求缺少 PKCE code_challenge — Claude 應自動帶上、若沒帶請聯絡 client。";
    case "unsupported_code_challenge_method":
      return "目前只支援 PKCE S256。";
    case "invalid_scope":
      return e.hint ?? "scope 不在允許範圍。";
    case "client_not_found":
      return "找不到該 OAuth client。";
    default:
      return `${e.code}（${e.correlationId ?? "—"}）`;
  }
}
